import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ruler, Building2, HardHat } from 'lucide-react';
import { publicApi } from '../lib/api';
import { resolveImageUrl } from '../lib/utils';
import PageSEO from '../components/PageSEO';

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await publicApi.getProjects();
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const getStatusLabel = (status) => {
    const labels = {
      planned: 'Planlanan',
      in_progress: 'Devam Ediyor',
      completed: 'Tamamlandı',
    };
    return labels[status] || status;
  };

  return (
    <div data-testid="projects-page" className="page-transition">
      <PageSEO
        title="Projelerimiz"
        description="VE PERA İnşaat projeleri — İstanbul'da değer yaratan, güvenli ve modern yaşam alanları. Devam eden ve yakında eklenecek projelerimizi keşfedin."
        path="/projeler"
      />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-20 bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <span className="overline mb-6 block">Projelerimiz</span>
              <h1
                className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[1] tracking-[-0.03em]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Değer yaratan,
                <br />
                <span className="italic font-light text-[#8e8e93]">iz bırakan</span> projeler
              </h1>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex items-end">
              <p
                className="text-[#8e8e93] leading-[1.9] text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                PERA İnşaat'ın vizyoner projeleri — güvenli yapılar, değerli yaşam alanları ve şehirlerin siluetine kalıcı dokunuşlar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Active Projects */}
      <section data-testid="projects-grid" className="section-padding bg-[#f2f2f7]">
        <div className="container-wide">
          {loading ? (
            <div className="grid lg:grid-cols-12 gap-8 animate-pulse">
              <div className="lg:col-span-7 bg-[#d1d1d6] h-[500px]"></div>
              <div className="lg:col-span-4 lg:col-start-9 flex flex-col justify-center gap-4">
                <div className="bg-[#d1d1d6] h-3 w-20"></div>
                <div className="bg-[#d1d1d6] h-8 w-64"></div>
                <div className="bg-[#d1d1d6] h-4 w-full"></div>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="py-24 text-center">
              <p className="text-[#8e8e93] text-[15px]" style={{ fontFamily: "var(--font-body)" }}>
                Projelerimiz yakında burada yer alacaktır.
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              {projects.map((project, index) => (
                <div
                  key={project.id}
                  data-testid={`project-item-${index}`}
                  className={`group grid lg:grid-cols-12 gap-8 lg:gap-12 py-16 lg:py-24 ${
                    index !== 0 ? 'border-t border-[#d1d1d6]' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`lg:col-span-7 ${index % 2 === 1 ? 'lg:order-2 lg:col-start-6' : ''}`}>
                    <div className="image-reveal aspect-[16/10] lg:aspect-[4/3] relative">
                      <img
                        src={resolveImageUrl(project.image_url)}
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                      <span
                        className="absolute bottom-0 left-0 right-0 py-2.5 px-4 text-[10px] tracking-[0.08em] text-white/60 bg-gradient-to-t from-black/40 to-transparent"
                        style={{ fontFamily: "var(--font-body)" }}
                      >
                        * Görsel temsilidir. Gerçek proje görselleriyle güncellenecektir.
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`lg:col-span-4 flex flex-col justify-center ${
                    index % 2 === 1 ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-9'
                  }`}>
                    <span
                      className={`badge-status self-start mb-6 ${
                        project.status === 'completed' ? 'badge-completed' :
                        project.status === 'in_progress' ? 'badge-in-progress' : 'badge-planned'
                      }`}
                    >
                      {getStatusLabel(project.status)}
                    </span>

                    <span
                      className="text-[11px] tracking-[0.15em] uppercase text-[#8e8e93]/60 mb-3 block"
                      style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                    >
                      {project.location}
                    </span>

                    <h2
                      className="text-[clamp(1.5rem,3vw,2.25rem)] leading-[1.15] tracking-[-0.02em] mb-5 group-hover:text-[#b87333] transition-colors duration-500"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                    >
                      {project.title}
                    </h2>

                    <p
                      className="text-[#8e8e93] leading-[1.9] text-[14px] mb-8"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {project.description}
                    </p>

                    {(project.area || project.units) && (
                      <div className="flex gap-12 mb-8">
                        {project.area && (
                          <div>
                            <span
                              className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/50 mb-1"
                              style={{ fontFamily: "var(--font-body)" }}
                            >
                              Alan
                            </span>
                            <span
                              className="text-[18px] text-[#1a1a1a]"
                              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                            >
                              {project.area}
                            </span>
                          </div>
                        )}
                        {project.units && (
                          <div>
                            <span
                              className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/50 mb-1"
                              style={{ fontFamily: "var(--font-body)" }}
                            >
                              Ünite
                            </span>
                            <span
                              className="text-[18px] text-[#1a1a1a]"
                              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                            >
                              {project.units}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="w-12 h-[1px] bg-[#041f60]/40 mb-5"></div>
                    <p
                      className="text-[#8e8e93]/50 text-[11px] italic leading-[1.6]"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      * Proje bilgileri ve görseller temsilidir. Gerçek proje detaylarıyla güncellenecektir.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon — Elegant placeholder */}
      <section className="bg-white py-24 lg:py-32">
        <div className="container-wide">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">Yakında</span>
            <h2
              className="text-[clamp(1.5rem,3vw,2.25rem)] leading-[1.2] tracking-[-0.02em] mb-4"
              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
            >
              Yeni projelerimiz hazırlanıyor
            </h2>
            <p
              className="text-[#8e8e93] leading-[1.8] text-[15px] max-w-lg mx-auto"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Portföyümüz sürekli genişliyor. Yeni projelerimiz tamamlandıkça bu sayfada yer alacaktır.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Card 1 — Planlanan */}
            <div className="group border border-[#d1d1d6] p-8 lg:p-10 hover:border-[#041f60]/30 transition-colors duration-500">
              <div className="w-12 h-12 rounded-full border border-[#d1d1d6] flex items-center justify-center mb-8 group-hover:border-[#b87333] transition-colors duration-500">
                <Ruler size={20} className="text-[#8e8e93] group-hover:text-[#b87333] transition-colors duration-500" />
              </div>
              <span
                className="text-[10px] tracking-[0.2em] uppercase text-[#b87333] mb-3 block"
                style={{ fontFamily: "var(--font-body)", fontWeight: 600 }}
              >
                Planlanan Projeler
              </span>
              <h3
                className="text-[20px] leading-[1.3] mb-4"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Tasarım aşamasında
              </h3>
              <p
                className="text-[#8e8e93] text-[14px] leading-[1.8]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Yeni projelerimizin mimari ve mühendislik çalışmaları devam etmektedir. Detaylar netleştikçe paylaşılacaktır.
              </p>
            </div>

            {/* Card 2 — Devam Eden */}
            <div className="group border border-[#041f60]/20 bg-[#041f60]/[0.02] p-8 lg:p-10">
              <div className="w-12 h-12 rounded-full border border-[#041f60]/30 flex items-center justify-center mb-8">
                <HardHat size={20} className="text-[#041f60]" />
              </div>
              <span
                className="text-[10px] tracking-[0.2em] uppercase text-[#041f60] mb-3 block"
                style={{ fontFamily: "var(--font-body)", fontWeight: 600 }}
              >
                Devam Eden Projeler
              </span>
              <h3
                className="text-[20px] leading-[1.3] mb-4"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Aktif olarak inşa ediliyor
              </h3>
              <p
                className="text-[#8e8e93] text-[14px] leading-[1.8]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Şu anda sahada aktif projelerimiz bulunmaktadır. İlerleme detayları düzenli olarak güncellenecektir.
              </p>
            </div>

            {/* Card 3 — Tamamlanan */}
            <div className="group border border-[#d1d1d6] p-8 lg:p-10 hover:border-[#041f60]/30 transition-colors duration-500">
              <div className="w-12 h-12 rounded-full border border-[#d1d1d6] flex items-center justify-center mb-8 group-hover:border-[#b87333] transition-colors duration-500">
                <Building2 size={20} className="text-[#8e8e93] group-hover:text-[#b87333] transition-colors duration-500" />
              </div>
              <span
                className="text-[10px] tracking-[0.2em] uppercase text-[#b87333] mb-3 block"
                style={{ fontFamily: "var(--font-body)", fontWeight: 600 }}
              >
                Tamamlanan Projeler
              </span>
              <h3
                className="text-[20px] leading-[1.3] mb-4"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Teslim edilen eserler
              </h3>
              <p
                className="text-[#8e8e93] text-[14px] leading-[1.8]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Tamamlanan projelerimiz ve referanslarımız yakında bu bölümde detaylı olarak yer alacaktır.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative h-[60vh] min-h-[450px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1772012454156-b6ed2fdb4c30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwYnVpbGRpbmclMjBmYWNhZGUlMjBtaW5pbWFsaXN0JTIwY29uY3JldGUlMjBnbGFzc3xlbnwwfHx8fDE3NzU0NjUwMTB8MA&ixlib=rb-4.1.0&q=85"
            alt="Architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/60"></div>
        </div>
        <div className="relative z-10 container-wide">
          <div className="max-w-xl">
            <h2
              className="text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.15] tracking-[-0.02em] text-[#f2f2f7] mb-8"
              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
            >
              Projenizi birlikte
              <br />hayata geçirelim
            </h2>
            <p
              className="text-[#d1d1d6]/80 leading-[1.8] mb-10 text-[15px] max-w-md"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Size özel çözümlerimizi keşfedin. Uzman ekibimiz sorularınızı yanıtlamak için hazır.
            </p>
            <Link to="/iletisim" className="btn-hero-primary">
              İletişime Geçin
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
